import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
} from '@angular/core';
import { FormGroup } from '@angular/forms';
import { Currency, UserDepositMethod } from '@freelancer/datastore/collections';
import { Deposits } from '@freelancer/deposits';
import {
  PaymentsEventType,
  PaymentsMessagingService,
} from '@freelancer/payments-messaging';
import {
  methodSelectionRadioControl,
  PaymentsUtils,
} from '@freelancer/payments-utils';
import { HeadingType } from '@freelancer/ui/heading';
import { PictureImage } from '@freelancer/ui/picture';
import { TextSize } from '@freelancer/ui/text';
import { DepositMethodApi } from 'api-typings/payments/payments';
import * as Rx from 'rxjs';
import { filter, map, startWith } from 'rxjs/operators';
import { SourceDisplayMode } from '../source-template/source-template.component';
import { DepositData, GenericPayload } from '../types';

@Component({
  selector: 'app-generic-method',
  template: `
    <app-source-template
      [group]="group"
      [sourceRadioValue]="id"
      [sourceDisplayMode]="sourceDisplayMode"
    >
      <app-source-template-header>
        <app-source-template-header-title>
          {{ label }}
        </app-source-template-header-title>
        <fl-text [size]="TextSize.SMALL" *ngIf="logoReplacementText">
          {{ logoReplacementText }}
        </fl-text>
        <app-source-template-logo
          *ngIf="!logoReplacementText"
          [image]="images.logo"
        ></app-source-template-logo>
      </app-source-template-header>
    </app-source-template>
  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
  styleUrls: ['./generic-method.component.scss'],
})
export class GenericMethodComponent implements OnInit, OnDestroy {
  HeadingType = HeadingType;
  TextSize = TextSize;
  SourceDisplayMode = SourceDisplayMode;

  images: { logo: PictureImage };
  private subscriptions: readonly Rx.Subscription[] = [];

  @Input() backUrlEncoded?: string;
  @Input() currencies: readonly Currency[];
  @Input() depositFormGroup: FormGroup;
  @Input() genericPayload: GenericPayload;
  @Input() group: FormGroup;
  @Input() id: string;
  @Input() label: string;
  @Input() enablePopUp = false;
  @Input() logoReplacementText = '';
  @Input() forceDefaultCurrencyAsPaymentCurrency = false;
  @Input() depositMethod$: Rx.Observable<UserDepositMethod>;
  @Input() sourceDisplayMode: SourceDisplayMode;
  @Input() set depositMethodId(depositMethodId: DepositMethodApi) {
    this._depositMethodId = depositMethodId;
    if (depositMethodId) {
      this.handleDepositMethodIdChanges(depositMethodId);
    }
  }

  @Input() set depositData(depositData: DepositData) {
    if (depositData) {
      this.handleDepositDataChanges(depositData);
    }
  }

  @Output() paymentCurrencySelected = new EventEmitter<Currency>();

  private _depositMethodId: DepositMethodApi;

  constructor(
    private paymentsMessaging: PaymentsMessagingService,
    private deposits: Deposits,
    private paymentsUtils: PaymentsUtils,
  ) {}

  ngOnInit() {
    const selected$ = this.group.valueChanges.pipe(
      startWith(this.group.value),
      map(value => value[methodSelectionRadioControl] === this.id),
    );
    if (this.forceDefaultCurrencyAsPaymentCurrency) {
      const emitPaymentCurrency$ = Rx.combineLatest([
        selected$.pipe(filter(selected => selected)),
        this.depositMethod$,
      ]).pipe(
        map(([_, methodData]) => {
          this.paymentCurrencySelected.emit(methodData.defaultCurrency);
        }),
      );
      this.subscriptions = [
        ...this.subscriptions,
        emitPaymentCurrency$.subscribe(),
      ];
    }
  }

  ngOnDestroy() {
    this.subscriptions.forEach(s => s.unsubscribe());
  }

  closeOverlay() {
    this.paymentsMessaging.pushEvent({
      eventType: PaymentsEventType.CLOSE_OVERLAY,
    });
  }

  handleDepositDataChanges(depositData: DepositData) {
    const payload = {
      ...this.genericPayload,
      deposit_method: this._depositMethodId,
      amount: depositData.depositAmount,
      currency_id: depositData.depositCurrency.id,
      confirm_url: this.deposits.getConfirmUrl({
        // If the pop up is disabled (IE), we need the backUrlEncoded as backUrl to do the redirection properly in backend.
        backUrl: this.enablePopUp
          ? this.genericPayload.back_url
          : this.backUrlEncoded,
        enablePopUp: this.enablePopUp,
      }),
    };
    this.deposits.makeDeposit({ payload, enablePopup: this.enablePopUp });
  }

  handleDepositMethodIdChanges(depositMethodId: DepositMethodApi) {
    this.images = {
      logo: {
        src: this.paymentsUtils.getMethodLogoPath(depositMethodId),
        alt: `${depositMethodId} logo`,
      },
    };
  }
}
